import { useMemo } from "react";
import type { Job, Issue } from "@/data/types";
import {
  isSameDay, parseISO, isValid, startOfWeek, endOfWeek, addDays,
} from "date-fns";
import { isUuidLike } from "@/lib/profileDisplayName";

export interface CrewMetrics {
  name: string;
  type: "Internal Crew" | "Subcontractor";
  todayJobs: number;
  weekJobs: number;
  totalAssigned: number;
  inProgress: number;
  completed: number;
  openIssues: number;
  /** 0-100 utilization score based on week job density */
  utilization: number;
  status: "overloaded" | "optimal" | "underutilized" | "idle";
}

const MAX_OPTIMAL_WEEKLY = 4; // jobs/week considered "optimal" ceiling

function getStatus(weekJobs: number): CrewMetrics["status"] {
  if (weekJobs === 0) return "idle";
  if (weekJobs <= 2) return "underutilized";
  if (weekJobs <= MAX_OPTIMAL_WEEKLY) return "optimal";
  return "overloaded";
}

export function useCrewUtilization(
  jobs: Job[],
  issues: Issue[],
  referenceDate: Date = new Date(),
  repNameById?: Map<string, string>,
): CrewMetrics[] {
  return useMemo(() => {
    const crewMap = new Map<string, {
      type: "Internal Crew" | "Subcontractor";
      todayJobs: number;
      weekJobs: number;
      totalAssigned: number;
      inProgress: number;
      completed: number;
      jobIds: Set<string>;
    }>();

    const wStart = startOfWeek(referenceDate, { weekStartsOn: 1 });
    const wEnd = endOfWeek(referenceDate, { weekStartsOn: 1 });
    const weekDays = Array.from({ length: 7 }, (_, i) => addDays(wStart, i));

    const activeJobs = (jobs ?? []).filter((j) => j.currentStage !== "Sale Closed");

    activeJobs.forEach((j) => {
      let crewName: string | null = null;
      let crewType: "Internal Crew" | "Subcontractor" = "Internal Crew";

      if (j.assignmentType === "Internal Crew" && j.assignedCrew) {
        crewName = j.assignedCrew;
        crewType = "Internal Crew";
      } else if (j.assignmentType === "Subcontractor" && j.assignedSubcontractor) {
        crewName = j.assignedSubcontractor;
        crewType = "Subcontractor";
      }
      if (!crewName) return;

      if (repNameById && isUuidLike(crewName)) {
        const resolved = repNameById.get(crewName);
        if (resolved) crewName = resolved;
      }

      if (!crewMap.has(crewName)) {
        crewMap.set(crewName, { type: crewType, todayJobs: 0, weekJobs: 0, totalAssigned: 0, inProgress: 0, completed: 0, jobIds: new Set() });
      }
      const entry = crewMap.get(crewName)!;
      entry.jobIds.add(j.id);

      if (j.currentStage === "Completed") {
        entry.completed++;
      } else {
        entry.totalAssigned++;
        if (j.currentStage === "In Progress") entry.inProgress++;
      }

      // Check scheduling
      if (j.scheduledInstallDate && isValid(parseISO(j.scheduledInstallDate))) {
        const d = parseISO(j.scheduledInstallDate);
        if (isSameDay(d, referenceDate)) entry.todayJobs++;
        if (d >= wStart && d <= wEnd) entry.weekJobs++;
      }
    });

    const openIssueMap = new Map<string, number>();
    (issues ?? [])
      .filter((i) => i.status === "Open" || i.status === "In Progress")
      .forEach((i) => {
        openIssueMap.set(i.jobId, (openIssueMap.get(i.jobId) || 0) + 1);
      });

    return [...crewMap.entries()]
      .map(([name, data]) => {
        let crewIssues = 0;
        data.jobIds.forEach((jid) => { crewIssues += openIssueMap.get(jid) || 0; });
        const utilization = Math.min(100, Math.round((data.weekJobs / MAX_OPTIMAL_WEEKLY) * 100));
        return {
          name,
          type: data.type,
          todayJobs: data.todayJobs,
          weekJobs: data.weekJobs,
          totalAssigned: data.totalAssigned,
          inProgress: data.inProgress,
          completed: data.completed,
          openIssues: crewIssues,
          utilization,
          status: getStatus(data.weekJobs),
        };
      })
      .sort((a, b) => b.weekJobs - a.weekJobs);
  }, [jobs, issues, referenceDate, repNameById]);
}
